import { useEffect, useRef, useContext, useMemo } from "react";
import CheckUpContext from "../../context/check-up";
import { useForm } from "react-hook-form";
import UserContext from "../../context/user";
import Question from "../question";
import {
  codiceFiscaleRegex,
  emailRegex,
  getLengthFromRegex,
  getSameEmailRegex,
} from "../../utils/regex";
import useCheckUpCreateMutation from "../../hooks/mutations/useCheckupCreateMutation";
import useCheckUpUpdateMutation from "../../hooks/mutations/useCheckupUpdateMutation";
import CheckUpDiscardRequestModal from "./check-up-discard-request-modal";
import { getGenderAnswer, getOptionalExamsFromAnswer, getQuestionId } from "../../utils/check-up";
import useFocusOnFirstError from "../../hooks/useFocusOnFirstError";

const CheckUpStep1 = ({ discardModal, setDiscardModal }) => {
  const { userProfile } = useContext(UserContext);
  const { survey, formData, labels, setFormData, currentSectionIndex, currentRequest } =
    useContext(CheckUpContext);

  const section = useMemo(
    () => survey?.definition?.sections[currentSectionIndex],
    [survey, currentSectionIndex]
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    setFocus,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  useFocusOnFirstError(errors, setFocus);

  const formWatch = watch();
  const initializedFormData = useRef();

  useEffect(() => {
    if (initializedFormData.current) {
      reset(formData);
    }
  }, [reset, formData]);

  useEffect(() => {
    if (!initializedFormData.current && section?.questions && userProfile) {
      initializedFormData.current = true;
      const initializedFields = {
        [section.questions?.[0]?.id]: userProfile?.firstName,
        [section.questions?.[1]?.id]: userProfile?.lastName,
      };
      setFormData((current) => ({ ...current, ...initializedFields }));
    }
  }, [userProfile, section, setFormData, survey]);

  const { mutateAsync: createRequest, isError: isCreateError } = useCheckUpCreateMutation();
  const { mutateAsync: updateRequest, isError } = useCheckUpUpdateMutation();

  const onSubmit = async (values) => {
    if (currentRequest?.surveySubmission?.submissionId) {
      const genderValue = values[getQuestionId(survey, "gender")];
      const isGenderUpdated =
        currentRequest?.gender && currentRequest?.gender !== getGenderAnswer(survey, genderValue);
      if (isGenderUpdated) {
        console.log("Gender has been updated, clearing optionals exams");
        values[getQuestionId(survey, "optionals")] = null;
      }

      await updateRequest({
        survey,
        request: currentRequest,
        formData,
        values,
        email: values[getQuestionId(survey, "mail")],
        phone: values[getQuestionId(survey, "phone")],
        gender: genderValue,
        dateOfBirth: values[getQuestionId(survey, "birthdate")],
        workplace: values[getQuestionId(survey, "workPlace")],
        birthPlace: values[getQuestionId(survey, "birthPlace")],
        taxCode: values[getQuestionId(survey, "fiscalCode")],
        preferredTimeSlot: values[getQuestionId(survey, "availability")],
        quadro: values[getQuestionId(survey, "quadro")],
        manager: values[getQuestionId(survey, "manager")],
        packageSelected: {
          optionalsExams: isGenderUpdated
            ? []
            : getOptionalExamsFromAnswer(formWatch, getQuestionId(survey, "optionals")),
          package: currentRequest?.packageSelected?.pack?.id,
        },
        lastCompletedStep: 0,
      });
    } else {
      await createRequest({
        survey,
        formData,
        values,
        email: values[getQuestionId(survey, "mail")],
        phone: values[getQuestionId(survey, "phone")],
        gender: values[getQuestionId(survey, "gender")],
        dateOfBirth: values[getQuestionId(survey, "birthdate")],
        workplace: values[getQuestionId(survey, "workPlace")],
        birthPlace: values[getQuestionId(survey, "birthPlace")],
        taxCode: values[getQuestionId(survey, "fiscalCode")],
        preferredTimeSlot: values[getQuestionId(survey, "availability")],
        quadro: values[getQuestionId(survey, "quadro")],
        manager: values[getQuestionId(survey, "manager")],
        lastCompletedStep: 0,
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form" key={section.id} noValidate>
        {section?.questions?.map((question, i) => (
          <Question
            question={question}
            key={question.id}
            register={register}
            error={errors[`${question.id}`]}
            touched={touchedFields[`${question.id}`]}
            isDirty={dirtyFields[`${question.id}`]}
            control={control}
            trigger={trigger}
            readOnlyMode={i < 2}
            labels={labels}
            pattern={
              i === 4
                ? codiceFiscaleRegex
                : i === 9
                ? emailRegex
                : i === 10
                ? getSameEmailRegex(formWatch[section.questions[9].id])
                : null
            }
            customPatternErrorMsg={
              i === 9
                ? labels?.WELFARE_NOT_VALID_EMAIL || "The inserted email is not valid"
                : i === 10
                ? labels?.WELFARE_NOT_CORRESPONDING_EMAIL ||
                  "The inserted email does not correspond"
                : null
            }
            maxLength={i === 4 ? getLengthFromRegex(codiceFiscaleRegex) : null}
            comment={
              i === 7
                ? { message: labels?.WELFARE_CHECKUP_PHONE_QUESTION_COMMENT }
                : i === 9
                ? { message: labels?.WELFARE_CHECKUP_MAIL_QUESTION_COMMENT }
                : null
            }
            trim={[9, 10].includes(i)}
            value={formWatch[`${question.id}`]}
            attachments={survey?.attachments}
            isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
          />
        ))}
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {(isError || isCreateError) && (
            <p className="form__error text-center">
              {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
            </p>
          )}
          <div className="form__buttons form__buttons--end">
            <button className="button primary form__submit">
              {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
            </button>
          </div>
        </div>
      </form>
      <CheckUpDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={formWatch}
        lastCompletedStep={-1}
      />
    </>
  );
};

export default CheckUpStep1;
